import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { parseIsoToDate, endOfDayLocal, startOfDayLocal, toIsoUtc } from '@helpers/dateUtils';
import DateTimePicker from '../DateTimePicker/DateTimePicker';

const DateColumn = styled.div`
  width: 170px;
`;

export interface DateFilterValues {
  startDate?: string | null;
  endDate?: string | null;
}

interface Props {
  value: DateFilterValues;
  onChange: (value: DateFilterValues) => void;
  isStartDisabled?: boolean;
  showTime?: boolean;
  startedPlaceholder?: string;
}

// Helper to convert an endDate with endOf('day') time back to start of day for display
function getDisplayDateFromEndDate(endDateStr: string): string {
  const date = parseIsoToDate(endDateStr);
  if (!date) return endDateStr;

  // If it's at the end of a day (23:59:59), return the start of that same day
  if (date.getHours() === 23 && date.getMinutes() === 59) {
    return toIsoUtc(startOfDayLocal(date));
  }
  return endDateStr;
}

// Helper to check if a date has a time component (not at start of day)
function hasTimeComponent(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const date = parseIsoToDate(dateStr);
  if (!date) return false;
  return date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
}

const DateFilterControlled: React.FC<Props> = ({
  value,
  onChange,
  isStartDisabled = false,
  showTime = false,
  startedPlaceholder,
}) => {
  const { t } = useTranslation();
  const { startDate, endDate } = value;

  // For date-only mode, derive display date from endDate (no state needed)
  const displayEndDate = endDate ? getDisplayDateFromEndDate(endDate) : null;

  const handleStartDateChange = (newValue: string, hasTime: boolean) => {
    if (!newValue) {
      onChange({ ...value, startDate: null });
      return;
    }

    // Value is already an ISO string from DateTimePicker
    // For date-only, ensure it's at start of day
    const filterDate = hasTime ? newValue : toIsoUtc(startOfDayLocal(newValue));

    onChange({ ...value, startDate: filterDate });
  };

  const handleEndDateChange = (newValue: string, hasTime: boolean) => {
    if (!newValue) {
      onChange({ ...value, endDate: null });
      return;
    }

    // For filtering purposes, use end-of-day when no time specified
    const filterDate = hasTime ? newValue : toIsoUtc(endOfDayLocal(newValue));

    onChange({ ...value, endDate: filterDate });
  };

  const displayedEndDate = showTime ? endDate : displayEndDate;

  return (
    <>
      <DateColumn className="column">
        <DateTimePicker
          title={t('entry.filter_starting')}
          name="startDateFilter"
          datePlaceholder={startedPlaceholder}
          dateTime={startDate && !isStartDisabled ? startDate : null}
          showTime={showTime}
          hasTime={showTime && hasTimeComponent(startDate)}
          isClearable={true}
          isDisabled={isStartDisabled}
          changed={handleStartDateChange}
        />
      </DateColumn>
      <DateColumn className="column">
        <DateTimePicker
          title={t('entry.filter_ending')}
          name="endDateFilter"
          dateTime={displayedEndDate || null}
          showTime={showTime}
          hasTime={showTime && hasTimeComponent(endDate)}
          isClearable={true}
          changed={handleEndDateChange}
        />
      </DateColumn>
    </>
  );
};

export default DateFilterControlled;
